import { environment } from '@core/environments/environment';
import { EEnvironmentName } from '@core/environments/environment.defaults';
import { WindowRedirectService } from '@core/services/window-redirect.service';

export interface IEarlyAppVersion {
  buildTime?: string;
  version: string;
}

const APP_UPDATE_BYPASS_PARAM = 'ngsw-bypass';
let earlyAppVersionCheck: Promise<IEarlyAppVersion | null> | null = null;
// window.location's own properties aren't configurable in real Chrome (spyOnProperty throws
// "hostname is not declared configurable" there), so the localhost guard below reads through this
// seam instead of window.location.hostname directly - see setHostnameOverrideForTests().
let hostnameOverrideForTests: string | null = null;

/**
 * Call before bootstrapApplication(). The request is fire-and-forget, so it gives the version
 * probe a head start without delaying Angular bootstrap. The caller compares `version` and
 * `buildTime` with the values compiled into the current bundle.
 */
export function startEarlyAppVersionCheck(): void {
  // useVersionFileAppUpdateFallback is the single kill switch for all version-file-based
  // detection (see AppUpdateService.checkEarlyAppVersion() and checkForVersionMismatch()) - gate
  // the fetch itself on it too, not just what AppUpdateService does with the result, so turning
  // the flag off actually stops the network request rather than just ignoring its answer.
  if (
    earlyAppVersionCheck ||
    typeof window === 'undefined' ||
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !environment.useVersionFileAppUpdateFallback ||
    typeof fetch !== 'function' ||
    environment.environmentName === EEnvironmentName.local ||
    getHostname().includes('localhost')
  ) {
    return;
  }

  try {
    const versionUrl: string = getVersionFileUrl();

    earlyAppVersionCheck = fetch(versionUrl, {
      cache: 'no-store',
      headers: {
        [APP_UPDATE_BYPASS_PARAM]: 'true',
      },
    })
      .then(async (response): Promise<IEarlyAppVersion | null> => {
        if (!response.ok) {
          return null;
        }

        const contentType: string = response.headers.get('content-type') ?? '';
        const responseText: string = await response.text();

        if (!contentType.includes('application/json') || responseText.trim().startsWith('<')) {
          return null;
        }

        const parsed: unknown = JSON.parse(responseText);

        if (!isEarlyAppVersion(parsed)) {
          return null;
        }

        return parsed;
      })
      .catch(() => null);
  } catch {
    earlyAppVersionCheck = Promise.resolve(null);
  }
}

/**
 * Resolves with a parsed server version file when available. Never rejects for a first-ever
 * visit, an unavailable version file, or if startEarlyAppVersionCheck() was not called.
 */
export function getEarlyAppVersionCheck(): Promise<IEarlyAppVersion | null> {
  return earlyAppVersionCheck ?? Promise.resolve(null);
}

/** Test-only: clears the cached check so startEarlyAppVersionCheck() can be re-invoked under different conditions. */
export function resetEarlyAppVersionCheckForTests(): void {
  earlyAppVersionCheck = null;
}

/** Test-only: seeds the cached result directly, bypassing the fetch and its guard entirely. */
export function setEarlyAppVersionCheckForTests(value: Promise<IEarlyAppVersion | null>): void {
  earlyAppVersionCheck = value;
}

/** Test-only: overrides the hostname the localhost guard checks. Pass null to go back to reading
 *  the real window.location.hostname. */
export function setHostnameOverrideForTests(hostname: string | null): void {
  hostnameOverrideForTests = hostname;
}

function getHostname(): string {
  return hostnameOverrideForTests ?? window.location.hostname;
}

function getVersionFileUrl(): string {
  const basePath: string = WindowRedirectService.getRootPath();
  const normalizedBasePath: string = basePath.endsWith('/') ? basePath : `${basePath}/`;

  return new URL('version.json', `${window.location.origin}${normalizedBasePath}`).toString();
}

function isEarlyAppVersion(value: unknown): value is IEarlyAppVersion {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as { buildTime?: unknown; version?: unknown };

  return (
    typeof candidate.version === 'string' &&
    candidate.version.length > 0 &&
    (candidate.buildTime === undefined || typeof candidate.buildTime === 'string')
  );
}
